import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, Chip, CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ModalIndices, TabIndices } from '../../../common/i-doc/i-doc.registry.ts';
import { PageCard } from '@components/common/PageCard.tsx';
import { PageHint } from '@components/common/PageHint.tsx';
import { PageToolbar } from '@components/common/PageToolbar.tsx';
import { useQueryClient } from '@tanstack/react-query';
import { EHI_OPTIONS_QUERY_KEY, useEhiOptions } from '@hooks/useEhiOptions.ts';
import RefreshIcon from '@mui/icons-material/Refresh';
import { ConfirmationDialog } from '@components/common/ConfirmationDialog.tsx';
import { UniversalPagination } from '@components/common/UniversalPagination.tsx';
import { BaseDialog } from '@components/common/BaseDialog.tsx';
import { EhiSnapshotDetailsModal } from './EhiSnapshotDetailsModal.tsx';
import { ehiService, EhiDailyRunBatchResult, EhiLevel, EhiSnapshotRow } from '@services/ehi.service.ts';
import { toastService } from '@services/toast.service.ts';

const LEVEL_COLORS: Record<EhiLevel, 'error' | 'warning' | 'info' | 'success'> = {
  low: 'error',
  unstable: 'warning',
  good: 'info',
  strong: 'success',
};

// Таб "Знімки": реєстр знімків EHI (1 запис = 1 знімок 1 користувача за 1 дату).
// Базова інформація про збережені знімки EHI. Перегляд + ручне оновлення за завершену дату (усі або вибраний у фільтрі користувач).
export const EhiSnapshotsTab: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [userId, setUserId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [ehiLevel, setEhiLevel] = useState('');
  const { levels, users } = useEhiOptions();
  const labelOf = (options: { value: string; label: string }[], value: string) => options.find((o) => o.value === value)?.label ?? value;
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);

  const [items, setItems] = useState<EhiSnapshotRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [details, setDetails] = useState<{ userId: number; date: string } | null>(null);
  const [refreshResult, setRefreshResult] = useState<EhiDailyRunBatchResult | null>(null);
  // Оновлення за дату (2026-09-25, замість "за сьогодні"): лише завершений день - максимум вчора (браузерна дата;
  // точна межа по поясу кожного користувача перевіряється на бекенді).
  const yesterday = (() => { const d = new Date(); d.setDate(d.getDate() - 1); return d.toLocaleDateString('sv-SE'); })();
  const [refreshDate, setRefreshDate] = useState(yesterday);

  const fetchSnapshots = useCallback(async () => {
    setLoading(true);
    try {
      const res = await ehiService.getSnapshots({
        page,
        limit,
        userId: userId ? Number(userId) : undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        ehiLevel: (ehiLevel as EhiLevel) || undefined,
      });
      setItems(res.items);
      setTotal(res.total);
    } catch {
      toastService.error(t('ehiJournal.loadError'));
    } finally {
      setLoading(false);
    }
  }, [page, limit, userId, dateFrom, dateTo, ehiLevel, t]);

  const handleRefreshDate = async () => {
    setConfirmOpen(false);
    setRefreshing(true);
    try {
      const result = await ehiService.refreshDate(refreshDate, userId ? Number(userId) : undefined);
      setRefreshResult(result);
      // після оновлення можуть з'явитись знімки нових користувачів - словник користувачів у кеші став застарілим
      await queryClient.invalidateQueries({ queryKey: EHI_OPTIONS_QUERY_KEY });
      await fetchSnapshots();
    } catch {
      toastService.error(t('ehiJournal.refreshError'));
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchSnapshots();
  }, [fetchSnapshots]);

  return (
    <Box data-i-doc={TabIndices.ADMIN_EHI_SNAPSHOTS}>
      <PageHint iDoc={TabIndices.ADMIN_EHI_SNAPSHOTS} />

      <PageCard>
        <PageToolbar
          storageKey="filters:admin-ehi-journal"
          filters={[
            {
              id: 'userId',
              type: 'select',
              label: t('ehiJournal.colUser'),
              value: userId,
              onChange: (v) => { setUserId(v); setPage(1); },
              options: [{ value: '', label: t('ehiJournal.all') }, ...users],
            },
            {
              id: 'dateFrom',
              type: 'custom',
              value: dateFrom,
              onChange: (v) => { setDateFrom(v); setPage(1); },
              renderCustom: (change) => (
                <TextField size="small" type="date" label={t('ehiJournal.dateFrom')} value={dateFrom}
                  onChange={(e) => change(e.target.value)} InputLabelProps={{ shrink: true }} />
              ),
            },
            {
              id: 'dateTo',
              type: 'custom',
              value: dateTo,
              onChange: (v) => { setDateTo(v); setPage(1); },
              renderCustom: (change) => (
                <TextField size="small" type="date" label={t('ehiJournal.dateTo')} value={dateTo}
                  onChange={(e) => change(e.target.value)} InputLabelProps={{ shrink: true }} />
              ),
            },
            {
              id: 'ehiLevel',
              type: 'select',
              label: t('ehiJournal.colLevel'),
              value: ehiLevel,
              onChange: (v) => { setEhiLevel(v); setPage(1); },
              options: [{ value: '', label: t('ehiJournal.all') }, ...levels],
            },
          ]}
          onReset={() => { setUserId(''); setDateFrom(''); setDateTo(''); setEhiLevel(''); setPage(1); }}
          showReset
          extraActions={
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField
                type="date"
                label={t('ehiJournal.refreshDateLabel')}
                value={refreshDate}
                onChange={(e) => setRefreshDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: yesterday }}
              />
              <Button
                variant="outlined"
                startIcon={refreshing ? <CircularProgress size={16} /> : <RefreshIcon />}
                disabled={refreshing}
                onClick={() => (refreshDate && refreshDate <= yesterday ? setConfirmOpen(true) : toastService.error(t('ehiJournal.refreshDateRequired')))}
              >
                {t('ehiJournal.refreshButton')}
              </Button>
            </Box>
          }
        />

        {loading ? (
          <Box display="flex" justifyContent="center" sx={{ mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : items.length === 0 ? (
          <Typography sx={{ mt: 4, textAlign: 'center' }}>{t('ehiJournal.empty')}</Typography>
        ) : (
          <TableContainer>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('ehiJournal.colDate')}</TableCell>
                  <TableCell>{t('ehiJournal.colUser')}</TableCell>
                  <TableCell align="right">{t('ehiJournal.colEhi')}</TableCell>
                  <TableCell>{t('ehiJournal.colLevel')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((row) => (
                  <TableRow key={`${row.userId}-${row.snapshotDate}`} hover sx={{ cursor: 'pointer' }} onClick={() => setDetails({ userId: row.userId, date: row.snapshotDate })}>
                    <TableCell>{row.snapshotDate}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{row.userName ?? row.userEmail ?? '—'}</Typography>
                      {row.userName && <Typography variant="caption" color="text.secondary">{row.userEmail}</Typography>}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={600}>{row.ehiValueDisplay}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" variant="outlined" color={LEVEL_COLORS[row.ehiLevel]} label={labelOf(levels, row.ehiLevel)} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <UniversalPagination
          page={page}
          count={Math.ceil(total / limit)}
          limit={limit}
          total={total}
          onPageChange={setPage}
          onLimitChange={(v) => { setLimit(v); setPage(1); }}
          storageKey="pagination:admin-ehi-journal"
        />
      </PageCard>

      <ConfirmationDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleRefreshDate}
        title={t('ehiJournal.refreshTitle', { date: refreshDate })}
        message={t('ehiJournal.refreshMessage', {
          date: refreshDate,
          scope: userId ? `${t('ehiJournal.refreshScopeUser')} (${labelOf(users, userId)})` : t('ehiJournal.refreshScopeAll'),
        })}
        confirmText={t('ehiJournal.refreshConfirm')}
        cancelText={t('ehiJournal.refreshCancel')}
      />

      <EhiSnapshotDetailsModal target={details} onClose={() => setDetails(null)} />

      <BaseDialog
        open={refreshResult !== null}
        onClose={() => setRefreshResult(null)}
        title={t('ehiJournal.resultTitle')}
        iDoc={ModalIndices.ADMIN_EHI_REFRESH_RESULT}
        maxWidth="xs"
        actions={<Button onClick={() => setRefreshResult(null)}>{t('ehiJournal.close')}</Button>}
      >
        {refreshResult && (
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', rowGap: 0.75, columnGap: 2 }}>
            {[
              [t('ehiJournal.resultCandidates'), refreshResult.candidates],
              [t('ehiJournal.resultProcessed'), refreshResult.processed],
              [t('ehiJournal.resultCollected'), refreshResult.collected],
              [t('ehiJournal.resultSkipped'), refreshResult.skipped],
              [t('ehiJournal.resultFailed'), refreshResult.failed],
            ].map(([label, value]) => (
              <React.Fragment key={String(label)}>
                <Typography variant="body2" color="text.secondary">{label}</Typography>
                <Typography variant="body2" fontWeight={600} textAlign="right">{value}</Typography>
              </React.Fragment>
            ))}
          </Box>
        )}
      </BaseDialog>
    </Box>
  );
};
